import { useEffect, useRef, useState } from 'react';
import './ProgressBar.css';
import { Tooltip } from '@mui/material';


const CustomTooltip = ({ children, title, placement }) => <Tooltip placement={placement} title={<div>{`${title}`}</div>}
  slotProps={{
    popper: {
      modifiers: [
        {
          name: 'offset',
          options: {
            offset: [0, -18],
          },
        },
      ],
    },
  }}
  arrow
>{children}</Tooltip>

/**
 * 
 * @param {number} max - The maximum value on the scale, representing 100% progress
 * @param {number} value - The current progress value
 * @param {string} color  - The solid color for the progress bar
 * @param {string[]} colorGradient - An array of colors for the gradient. Overrides the solid color if provided
 * @returns 
 */

export const ProgressBar = ({ max, min, value, remaining, color, colorGradient, solidOverflow, showValueLabel, maxMap, valueMap, remainingMap, tooltip }) => {
  const _min = min ?? 0;
  const _max = max ?? 1;
  const range = _max - _min;
  const ratio = (value - _min) / (range);
  const barPercentage = Math.min(ratio * 100, 100);
  // const remaining = Math.max(0, _max - value);
  const valueLabelRef = useRef();
  const remainingRef = useRef();
  const rangeRef = useRef();

  const style =
    colorGradient?.length > 0 ?
      ((solidOverflow && ratio >= 1) ? { background: colorGradient[colorGradient.length - 1] } : { '--progress-bar-component-custom-gradient': `linear-gradient(to right, ${colorGradient.join(',')})` }) :
      (color ? { background: color } : {});
  // const labelStyle = {
  //   "grid-template-columns" : `${barPercentage}% 0px $${100 - barPercentage}%`
  // };
  const [labelStyle, setLabelStyle] = useState({});
  const [hideRemaining, setHideRemaining] = useState(false);

  useEffect(() => {
    let rangeWidth = rangeRef.current?.getBoundingClientRect().width ?? 1;
    let elementWidth = 100 * ((valueLabelRef.current?.getBoundingClientRect().width ?? 0) / rangeWidth);
    let remainingWidth = 100 * ((remainingRef.current?.getBoundingClientRect().width ?? 0) / rangeWidth);
    let percentage_ = (ratio) * 100;
    let labelColor = colorGradient ? interpolateColor(colorGradient, Math.min(ratio, 1)) : color;
    if (percentage_ > 50) {
      let calculatedRight = Math.max(0, (100 - percentage_) - (elementWidth / 2));
      setHideRemaining((remaining ?? null) ? (calculatedRight <= remainingWidth) : true);
      setLabelStyle({ 'right': `${calculatedRight}%`, color: labelColor });
    } else {
      let calculatedLeft = Math.max(0, percentage_ - (elementWidth / 2));
      setHideRemaining((remaining ?? null) ? (calculatedLeft + elementWidth >= (100 - remainingWidth)) : true);
      setLabelStyle({ 'left': `${calculatedLeft}%`, color: labelColor });
    }

  }, [value, _max, remaining, valueLabelRef, rangeRef, remainingRef]);


  // const aux = {"background": "cyan", "grid-template-columns":`${barPercentage}% 0 ${100 - barPercentage}%`};


  return (
    <CustomTooltip title={`${tooltip ?? value}`} placement={'bottom'}>
      <div className="progress-bar-component">
        {showValueLabel && <div className='value-label-container'>
          <span ref={valueLabelRef} style={{ ...labelStyle }}>{(valueMap ?? defaultMapper)(value)}</span>
    
        </div>}
        <div className="bar-range" ref={rangeRef} >
          <div className='bar-value' style={{ width: `${barPercentage.toFixed(1)}%`, ...style }}></div>
          {/* <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg"> <rect x="50%" y="0" width="50%" height="100%" fill="var(--grey-50)" /> </svg> */}
          {/* <svg width="100%" height="100%"> <mask id="progressMask"> <rect x="0" y="0" width="100%" height="100%" fill="white" /> <rect x={`${barPercentage}%`} y="0" width={`${100 - barPercentage}%`} height="100%" fill="black" /> </mask> <rect x="0" y="0" width="100%" height="100%" mask="url(#progressMask)" fill="currentColor" /> </svg> */}
          {/* <div className="bar-value" style={{width: `${Math.min((value/max) * 100, 100).toFixed(1)}%`, background: (colorGradient?.length > 0 ? `linear-gradient(to right, ${colorGradient.join(',')})` : null) ?? color ?? 'unset'}}> */}
          {/* <div className="bar-value"> */}
          {/* </div> */}
        </div>
        <div className='max-container'>
          {/* <CustomTooltip title="Maximum" placement="bottom" > */}
          <span>{(maxMap ?? defaultMapper)(_max)}</span>
          {/* </CustomTooltip> */}
        </div>
      </div>
    </CustomTooltip>
  )
}

function defaultMapper(x) { return x }

function interpolateColor(colorArray, ratio) {
  if (ratio < 0) {
    return colorArray[0];
  }

  if (ratio > 1) {
    return colorArray[colorArray.length - 1];
  }
  let normalizedIndex = normalizeIndex(colorArray);
  let pair = getAdjacentPairs(normalizedIndex).find(([i1, i2]) => {
    let t = (ratio - i1) / (i2 - i1);
    return t >= 0 && t <= 1;
  });
  let [color1, color2] = pair.map(i => colorArray[normalizedIndex.indexOf(i)]);
  let t = (ratio - pair[0]) / (pair[1] - pair[0]);
  return interpolateHexColor(color1, color2, t);
}

function interpolateHexColor(hex1, hex2, t) {
  const hexToRgb = (hex) => hex.match(/\w\w/g).map(x => parseInt(x, 16));
  const rgbToHex = (rgb) => `#${rgb.map(x => x.toString(16).padStart(2, '0')).join('')}`;
  const interpolate = (a, b, t) => Math.round(a + (b - a) * t);
  const [r1, g1, b1] = hexToRgb(hex1);
  const [r2, g2, b2] = hexToRgb(hex2);
  return rgbToHex([interpolate(r1, r2, t), interpolate(g1, g2, t), interpolate(b1, b2, t)]);
}

function getAdjacentPairs(array) { return array.slice(0, -1).map((_, i) => [array[i], array[i + 1]]); }

function normalizeIndex(array) {
  return array.map((_, i) => i / (array.length - 1));
}